<template>
  <form class="fin-panel-body" novalidate @submit.prevent="submit">
    <ui-segment
      block
      :value="isPayout ? 'payout' : 'payment'"
      :options="[
        { value: 'payout', label: $t('finance_toggle_payout') },
        { value: 'payment', label: $t('finance_toggle_payment') },
      ]"
      @input="setDirection"
    />

    <template v-if="isPayout">
      <div class="fin-row2">
        <div class="ui-field">
          <label for="fin-rec-from">{{ $t("finance_period_start") }}</label>
          <input id="fin-rec-from" v-model="from" type="date" class="ui-input" :max="to || undefined" />
        </div>
        <div class="ui-field">
          <label for="fin-rec-to">{{ $t("finance_period_end") }}</label>
          <input id="fin-rec-to" v-model="to" type="date" class="ui-input" :min="from || undefined" />
        </div>
      </div>
      <span v-if="tried && !periodValid" class="ui-err" role="alert">{{ $t("finance_required_field") }}</span>

      <div class="fin-calc" :aria-busy="String(loadingOverview)">
        <div>
          <small>{{ $t("finance_net_for_payout") }}</small>
          <b class="fin-num fin-calc__big">{{ calcText(net) }}</b>
        </div>
        <div><small>{{ $t("finance_turnover") }}</small><b class="fin-num">{{ calcText(turnover) }}</b></div>
        <div><small>{{ $t("finance_deductions") }}</small><b class="fin-num">{{ calcText(deductions) }}</b></div>
      </div>
    </template>

    <div class="ui-field">
      <label for="fin-rec-amount">{{ $t("finance_payment_amount") }} ({{ currency }})</label>
      <input id="fin-rec-amount" v-model="amount" class="ui-input fin-num" inputmode="decimal" autocomplete="off" :aria-invalid="String(tried && !(amountValue > 0))" @input="touched = true" />
      <span v-if="tried && !(amountValue > 0)" class="ui-err" role="alert">{{ $t("finance_amount_must_be_positive") }}</span>
    </div>

    <template v-if="differs">
      <div class="ui-banner ui-banner--info ui-banner--warn">
        <v-icon size="16" color="currentColor">mdi-alert-outline</v-icon><span>{{ $t("finance_amount_differs_warning") }}</span>
      </div>
      <div class="ui-field">
        <label for="fin-rec-reason">{{ $t("finance_difference_reason") }}</label>
        <input id="fin-rec-reason" v-model="reason" class="ui-input" autocomplete="off" :aria-invalid="String(tried && !reason.trim())" />
        <span v-if="tried && !reason.trim()" class="ui-err" role="alert">{{ $t("finance_required_field") }}</span>
      </div>
    </template>

    <div class="fin-row2">
      <div class="ui-field">
        <label for="fin-rec-date">{{ $t("finance_date") }}</label>
        <input id="fin-rec-date" v-model="date" type="date" class="ui-input" />
      </div>
      <div class="ui-field">
        <label for="fin-rec-method">{{ $t("finance_payment_method_label") }}</label>
        <select id="fin-rec-method" v-model="method" class="ui-input" :aria-invalid="String(tried && !method)">
          <option value="">{{ $t("finance_shell.choose") }}</option>
          <option value="bank">{{ $t("finance_shell.method.bank") }}</option>
          <option value="cash">{{ $t("finance_shell.method.cash") }}</option>
        </select>
        <span v-if="tried && !method" class="ui-err" role="alert">{{ $t("finance_required_field") }}</span>
      </div>
    </div>
    <div class="ui-field">
      <label for="fin-rec-ref">{{ $t("finance_reference") }}</label>
      <input id="fin-rec-ref" v-model="reference" class="ui-input" autocomplete="off" />
    </div>

    <div v-if="balance !== null" class="fin-preview" aria-live="polite">
      <span>
        {{ isPayout ? $t("finance_shell.record.you_pay") : $t("finance_shell.record.you_record") }}
        <b class="fin-num">{{ money(amountValue) }}</b><template v-if="method"> ({{ methodText }})</template>
      </span>
      <span class="ui-muted">
        {{ $t("finance_shell.record.balance_after") }}
        <b class="fin-num" :class="after >= 0 ? 'is-in' : 'is-out'">{{ after >= 0 ? $t("finance_shell.record.restaurant_receives") : $t("finance_shell.record.restaurant_owes") }} {{ money(Math.abs(after)) }}</b>
      </span>
    </div>

    <div v-if="error" class="ui-banner ui-banner--info fin-note--bad" role="alert"><v-icon size="16" color="currentColor">mdi-alert-circle-outline</v-icon><span>{{ error }}</span></div>

    <button type="submit" class="ui-btn ui-btn--primary ui-btn--block" :disabled="saving">
      {{ isPayout ? $t("finance_record_payout") : $t("finance_record_payment") }}
    </button>
  </form>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import { formatMoney } from "@/components/restaurant/finance/constants";
import { parseOverview } from "@/components/restaurant/finance/overview";
import { addDays, firstOfMonth, localIso, parseAmount, r2 } from "../financeLogic";

const TOLERANCE = 0.005;

// UUID pri OTVARANJU panela (ne pri kliku): isti kroz sve pokušaje istog unosa (dupli klik, ponovljen zahtjev).
const newKey = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

/**
 * "Evidentiraj isplatu / uplatu" (samo administrator), panel na zahtjev. Isplata: backend sam računa obračun iz perioda
 * (period_from, period_to), a administrator šalje samo ono što je stvarno plaćeno; razlika od neta traži razlog.
 * Prije snimanja se vidi "saldo poslije". Uplata restorana Orderi nema period.
 */
export default {
  name: "FinanceRecordPanel",
  inject: { shell: "reportShell" },
  props: {
    restaurantId: { type: [Number, String], default: null },
    currency: { type: String, default: "KM" },
    /** trenutni saldo restorana (za pregled "saldo poslije") */
    balance: { type: Number, default: null },
    /** period procjene { from, to } koji se predlaže */
    range: { type: Object, default: null },
  },
  data() {
    const today = localIso();
    const lastEnd = addDays(firstOfMonth(today), -1);
    return {
      direction: "payout",
      from: this.range ? this.range.from : firstOfMonth(lastEnd),
      to: this.range ? this.range.to : lastEnd,
      overview: null,
      loadingOverview: false,
      amount: "",
      touched: false,
      reason: "",
      date: today,
      method: "",
      reference: "",
      tried: false,
      saving: false,
      error: "",
      key: newKey(),
      seq: 0,
    };
  },
  computed: {
    isPayout() {
      return this.direction === "payout";
    },
    periodValid() {
      return Boolean(this.from && this.to && this.from <= this.to);
    },
    net() {
      return this.overview && this.overview.net !== null && this.overview.net !== undefined ? r2(this.overview.net) : null;
    },
    turnover() {
      return this.overview && this.overview.turnover !== null && this.overview.turnover !== undefined ? this.overview.turnover : null;
    },
    deductions() {
      return this.overview && this.overview.deductions !== null && this.overview.deductions !== undefined ? r2(this.overview.deductions) : null;
    },
    amountValue() {
      return parseAmount(this.amount);
    },
    differs() {
      return this.isPayout && this.net !== null && this.amount !== "" && Math.abs(this.amountValue - this.net) > TOLERANCE;
    },
    after() {
      return this.isPayout ? r2((this.balance || 0) - this.amountValue) : r2((this.balance || 0) + this.amountValue);
    },
    methodText() {
      return this.method === "bank" ? this.$t("finance_shell.method.bank") : this.$t("finance_shell.method.cash");
    },
  },
  watch: {
    from: "loadOverview",
    to: "loadOverview",
  },
  created() {
    this.loadOverview();
  },
  methods: {
    money(value) {
      return formatMoney(value, this.currency);
    },
    calcText(value) {
      return value === null ? "—" : formatMoney(value, this.currency);
    },
    setDirection(direction) {
      this.direction = direction;
      this.error = "";
      this.tried = false;
      this.reason = "";
      this.touched = false;
      this.amount = direction === "payout" && this.net !== null && this.net > 0 ? this.net.toFixed(2).replace(".", ",") : "";
    },
    async loadOverview() {
      if (!this.isPayout || !this.periodValid || !this.restaurantId) {
        return;
      }
      const seq = ++this.seq;
      this.loadingOverview = true;
      try {
        const res = await restaurantFinanceService.getFinancialOverview(this.restaurantId, { from: this.from, to: this.to });
        if (seq !== this.seq) return;
        this.overview = parseOverview((res.data && res.data.data) || res.data || {}, this.currency);
        // iznos je unaprijed neto; ne gazi se ako ga je administrator već mijenjao
        if (!this.touched) {
          this.amount = this.net !== null && this.net > 0 ? this.net.toFixed(2).replace(".", ",") : "";
        }
      } catch (error) {
        if (seq === this.seq) this.overview = null;
      } finally {
        if (seq === this.seq) this.loadingOverview = false;
      }
    },
    async submit() {
      this.tried = true;
      this.error = "";
      if (this.saving) return;
      if (!(this.amountValue > 0) || !this.method || (this.isPayout && !this.periodValid) || (this.differs && !this.reason.trim())) {
        return;
      }
      this.saving = true;
      try {
        if (this.isPayout) {
          const payload = { period_from: this.from, period_to: this.to, date: this.date, method: this.method, amount: this.amountValue, idempotency_key: this.key };
          if (this.reference) payload.reference = this.reference;
          if (this.differs) payload.note = this.reason.trim();
          await restaurantFinanceService.createPayout(this.restaurantId, payload);
          this.shell.showToast(this.$t("finance_shell.record.payout_created"));
        } else {
          const payload = { amount: this.amountValue, date: this.date, method: this.method, idempotency_key: this.key };
          if (this.from) payload.period = String(this.from).slice(0, 7);
          if (this.reference) payload.note = this.reference;
          await restaurantFinanceService.createPlatformPayment(this.restaurantId, payload);
          this.shell.showToast(this.$t("finance_shell.record.payment_saved"));
        }
        this.shell.closePanel();
        this.shell.reload();
      } catch (error) {
        const response = error && error.response && error.response.data;
        const first = response && response.errors ? Object.values(response.errors).flat()[0] : null;
        this.error = first || (response && response.message) || this.$t(this.isPayout ? "finance_payout_create_error" : "finance_payment_save_error");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
